import { Component, Renderer2, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { AppTopbar } from './app/layout/component/app.topbar';
import { AppSidebar } from './app/layout/component/app.sidebar';
import { AppSubmenuPanel } from './app/layout/component/submenu-panel/app.submenu-panel';
import { AppBillingBanner } from './app/layout/component/app.billing-banner';
import { SubmenuStateService } from './app/layout/component/submenu-panel/submenu-state.service';
import { LayoutService } from './app/layout/service/layout.service';
import { TokenService } from './app/pages/auth/token.service';
import { SubscriptionService } from './app/pages/service/core/subscription.service';
import { VerticalConfigService } from './app/pages/service/core/vertical-config.service';
import { PermissionStore } from './app/pages/service/core/permission-store';
import { AppScreenReadonlyBanner } from './app/layout/component/app.screen-readonly-banner';
import { ChatDockComponent } from './app/pages/conversation/components/chat-dock/chat-dock.component';

@Component({
    selector: 'app-root',
    standalone: true,
    imports: [CommonModule, AppTopbar, AppSidebar, AppSubmenuPanel, AppBillingBanner, AppScreenReadonlyBanner, ChatDockComponent, RouterModule],
    template: `
        <div class="layout-wrapper" [ngClass]="containerClass" [class.layout-menu-mini]="isPanelOpen" *ngIf="showLayout; else plainOutlet">
            <app-topbar></app-topbar>
            <app-sidebar></app-sidebar>
            <div class="layout-main-container">
                <div class="layout-main-row">
                    <app-submenu-panel></app-submenu-panel>
                    <div class="layout-main" [class.main--sheet]="sheetRoute" [class.screen-readonly]="viewOnly">
                        <app-billing-banner></app-billing-banner>
                        <app-screen-readonly-banner></app-screen-readonly-banner>
                        <router-outlet></router-outlet>
                    </div>
                </div>
            </div>
            <!-- Floating chat dock. Mounted HERE, in the root component, because this is the
                 real application shell — app/layout/component/app.layout.ts looks like the shell
                 but nothing references it, so anything placed there is tree-shaken away. Sitting
                 inside the showLayout branch keeps it off the login / public pages. Rendered once,
                 so open chats survive navigation; its own service decides whether to show. -->
            <app-chat-dock></app-chat-dock>
            <div class="layout-mask animate-fadein"></div>
        </div>
        <ng-template #plainOutlet>
            <router-outlet></router-outlet>
        </ng-template>
    `
})
export class AppComponent {
    overlayMenuOpenSubscription: Subscription;
    private submenuSubscription?: Subscription;
    private permsSubscription?: Subscription;
    menuOutsideClickListener: any;
    showLayout = true;
    isPanelOpen = false;
    /** Current screen is granted View but not Edit → global read-only mode: action
     *  buttons hidden + fields disabled (see .screen-readonly rules in styles.scss). */
    viewOnly = false;
    /** When true, the active page's content is wrapped in a white "sheet" panel (matches
     *  the Performance page). Toggled per-route from {@link sheetPrefixes}. */
    sheetRoute = false;

    /** Route prefixes whose pages (and any sub-routes) render inside the white sheet: the
     *  standalone dashboards + the Marketing / Channels / Analytics / Database sections.
     *  A few extras are matched precisely in {@link isSheetRoute} instead. */
    private readonly sheetPrefixes = [
        '/lead-dashboard', '/field-tracking', '/attendance',
        '/campaigns',
        '/channels', '/whatsapp-templates', '/whatsapp-automation',
        '/email-templates', '/email-sequences', '/email-automation', '/email-config',
        '/operations-reports', '/journey-analytics',
        '/database', '/vendors', '/customers', '/bulk-uploads', '/lead-import',
        '/commission',
    ];

    /** True when {@code url} should render inside the white "sheet" panel. Most sections
     *  match by path prefix ({@link sheetPrefixes}); a few extras are matched precisely so
     *  they don't drag in neighbours: the AI-Engine dashboard, the website-builder LIST +
     *  templates (never the full-screen editor / preview), and the whole Settings screen —
     *  every tab (My Profile, Company Profile, Channels config, …) now sheets page-by-page. */
    private isSheetRoute(url: string): boolean {
        const path = (url || '').split(/[?#]/)[0];
        if (this.sheetPrefixes.some(p => path === p || path.startsWith(p + '/'))) return true;
        return path === '/dashboard'
            || path === '/website-builder'
            || path === '/website-builder/templates'
            || path === '/settings';
    }
    /** Guard so the company's vertical theme is fetched from the server once. */
    private verticalApplied = false;

    @ViewChild(AppSidebar) appSidebar!: AppSidebar;
    @ViewChild(AppTopbar) appTopBar!: AppTopbar;

    constructor(
        public layoutService: LayoutService,
        public renderer: Renderer2,
        public router: Router,
        private tokenService: TokenService,
        private subscriptionService: SubscriptionService,
        private submenuState: SubmenuStateService,
        private verticalConfig: VerticalConfigService,
        private permissionStore: PermissionStore
    ) {
        this.submenuSubscription = this.submenuState.activeEntry().subscribe(entry => {
            this.isPanelOpen = !!entry;
        });
        this.showLayout = this.shouldShowLayout(this.router.url);
        this.sheetRoute = this.isSheetRoute(this.router.url);
        this.applyReadonlyMode(this.router.url);
        // Permissions load async after login — re-evaluate the read-only mode once they land
        // (and whenever they change), not just on navigation.
        this.permsSubscription = this.permissionStore.changed$.subscribe(() => this.applyReadonlyMode(this.router.url));
        // Skin the app to the company's vertical (theme + vocabulary). The service
        // already painted the cached choice on construct (no flash); this fetches
        // the authoritative value once we know the user is logged in.
        this.applyVerticalOnce();
        this.loadPermissionsOnce();

        this.overlayMenuOpenSubscription = this.layoutService.overlayOpen$.subscribe(() => {
            if (!this.menuOutsideClickListener) {
                this.menuOutsideClickListener = this.renderer.listen('document', 'click', (event) => {
                    if (this.isOutsideClicked(event)) {
                        this.hideMenu();
                    }
                });
            }

            if (this.layoutService.layoutState().staticMenuMobileActive) {
                this.blockBodyScroll();
            }
        });

        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event) => {
            const nav = event as NavigationEnd;
            this.showLayout = this.shouldShowLayout(nav.urlAfterRedirects);
            this.sheetRoute = this.isSheetRoute(nav.urlAfterRedirects);
            this.applyReadonlyMode(nav.urlAfterRedirects);
            this.hideMenu();
            // Covers the fresh-login case: AppComponent is built before login, so
            // re-check on entering the authed shell.
            this.applyVerticalOnce();
            this.loadPermissionsOnce();
        });
    }

    /** Fetch + apply the company vertical theme once we're logged in. No-op after
        the first successful trigger; safe to call on every navigation. Like
        loadPermissionsOnce, the guard RESETS while logged out — otherwise a
        logout → login into another company kept the previous tenant's logo /
        vocabulary / pro-mode until a hard reload re-built the app. */
    private applyVerticalOnce(): void {
        if (!this.tokenService.isLoggedIn()) {
            if (this.verticalApplied) { this.verticalApplied = false; this.verticalConfig.clear(); }
            return;
        }
        if (this.verticalApplied || this.isSuperAdmin()) return;
        this.verticalApplied = true;
        this.verticalConfig.loadAndApply();
    }

    private permissionsLoaded = false;
    /** Load the user's screen permissions once after login; reset on logout. */
    private loadPermissionsOnce(): void {
        if (!this.tokenService.isLoggedIn()) {
            if (this.permissionsLoaded) { this.permissionsLoaded = false; this.permissionStore.clear(); }
            return;
        }
        if (this.permissionsLoaded || this.isSuperAdmin()) return;
        this.permissionsLoaded = true;
        this.permissionStore.load();
    }

    /** Platform super admins have no company, so company-scoped bootstrap is skipped. */
    private isSuperAdmin(): boolean {
        return this.tokenService.getRoles().includes('ROLE_SUPER_ADMIN');
    }

    /** Toggle the global read-only mode for the current screen (View without Edit).
     *  The body class makes appendTo="body" overlays (dialogs, drawers, popovers)
     *  follow the same hide-buttons / disable-fields rules as the page content. */
    private applyReadonlyMode(url: string): void {
        this.viewOnly = this.permissionStore.isViewOnlyUrl(url);
        document.body.classList.toggle('screen-readonly-overlays', this.viewOnly);
    }

    ngOnInit() {
        if (this.tokenService.isLoggedIn() && !this.isSuperAdmin() && !this.subscriptionService.getSubscriptionSnapshot()) {
            this.subscriptionService.fetchSubscription().subscribe({
                error: () => {}
            });
        }
    }

    shouldShowLayout(url: string): boolean {
        const cleanUrl = (url || '').split('?')[0].split('#')[0];
        if (cleanUrl === '' || cleanUrl === '/') {
            return false;
        }
        // '/sign' and '/case-status' are PUBLIC pages opened by customers. Beyond looking wrong,
        // rendering the shell there fires the topbar's authed calls (profile, unread counts); those
        // 401 for an anonymous visitor, which used to bounce them to the login screen.
        return !['/auth', '/landing', '/website-template', '/notfound', '/webchat-widget', '/webchat-center',
                 '/set-password', '/sign', '/case-status'].some((prefix) => cleanUrl.startsWith(prefix));
    }

    isOutsideClicked(event: MouseEvent) {
        const sidebarEl = document.querySelector('.layout-sidebar');
        const topbarEl = document.querySelector('.layout-menu-button');
        const eventTarget = event.target as Node;

        return !(
            sidebarEl?.isSameNode(eventTarget) ||
            sidebarEl?.contains(eventTarget) ||
            topbarEl?.isSameNode(eventTarget) ||
            topbarEl?.contains(eventTarget)
        );
    }

    hideMenu() {
        this.layoutService.layoutState.update((prev) => ({
            ...prev,
            overlayMenuActive: false,
            staticMenuMobileActive: false,
            menuHoverActive: false
        }));
        if (this.menuOutsideClickListener) {
            this.menuOutsideClickListener();
            this.menuOutsideClickListener = null;
        }
        this.unblockBodyScroll();
    }

    blockBodyScroll(): void {
        if (document.body.classList) {
            document.body.classList.add('blocked-scroll');
        } else {
            document.body.className += ' blocked-scroll';
        }
    }

    unblockBodyScroll(): void {
        if (document.body.classList) {
            document.body.classList.remove('blocked-scroll');
        } else {
            document.body.className = document.body.className.replace(new RegExp('(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
        }
    }

    get containerClass() {
        return {
            'layout-overlay': this.layoutService.layoutConfig().menuMode === 'overlay',
            'layout-static': this.layoutService.layoutConfig().menuMode === 'static',
            'layout-static-inactive': this.layoutService.layoutState().staticMenuDesktopInactive && this.layoutService.layoutConfig().menuMode === 'static',
            'layout-overlay-active': this.layoutService.layoutState().overlayMenuActive,
            'layout-mobile-active': this.layoutService.layoutState().staticMenuMobileActive
        };
    }

    ngOnDestroy() {
        if (this.overlayMenuOpenSubscription) {
            this.overlayMenuOpenSubscription.unsubscribe();
        }

        if (this.submenuSubscription) {
            this.submenuSubscription.unsubscribe();
        }

        if (this.permsSubscription) {
            this.permsSubscription.unsubscribe();
        }

        if (this.menuOutsideClickListener) {
            this.menuOutsideClickListener();
        }
    }
}
